import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  hasAllPermissions as checkAllPermissions,
  generatePermission,
  Module,
  PermissionAction
} from '../constants/permissions';

interface PermissionCheck {
  module: Module | string;
  action: PermissionAction;
}

interface UsePermissionReturn {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasModulePermission: (check: PermissionCheck) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyModulePermission: (module: Module | string) => boolean;
  isAdmin: boolean;
}

export const usePermission = (): UsePermissionReturn => {
  const { permissions } = useAuth();

  // 檢查是否為管理員
  const isAdmin = useMemo(() => {
    return permissions.includes('ADMIN');
  }, [permissions]);

  // 檢查單一權限
  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      return checkPermission(permissions, permission);
    };
  }, [permissions]);

  // 檢查模組權限
  const hasModulePermission = useMemo(() => {
    return ({ module, action }: PermissionCheck): boolean => {
      const permission = generatePermission(module as Module, action);
      return checkPermission(permissions, permission);
    };
  }, [permissions]);

  // 檢查多個權限（任一滿足）
  const hasAnyPermission = useMemo(() => {
    return (requiredPermissions: string[]): boolean => {
      return checkAnyPermission(permissions, requiredPermissions);
    };
  }, [permissions]);

  // 檢查多個權限（全部滿足）
  const hasAllPermissions = useMemo(() => {
    return (requiredPermissions: string[]): boolean => {
      return checkAllPermissions(permissions, requiredPermissions);
    };
  }, [permissions]);

  // 檢查是否有模組的任何權限
  const hasAnyModulePermission = useMemo(() => {
    return (module: Module | string): boolean => {
      return permissions.some(perm => perm.startsWith(`${module}_`));
    };
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    hasModulePermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyModulePermission,
    isAdmin
  };
};

// 匯出便利函數
export const useModulePermission = (module: Module | string) => {
  const { hasModulePermission, hasAnyModulePermission } = usePermission();
  
  return {
    canView: hasModulePermission({ module, action: 'query' }),
    canCreate: hasModulePermission({ module, action: 'add' }),
    canEdit: hasModulePermission({ module, action: 'edit' }),
    canDelete: hasModulePermission({ module, action: 'delete' }),
    canExport: hasModulePermission({ module, action: 'export' }),
    canPrint: hasModulePermission({ module, action: 'print' }),
    canApprove: hasModulePermission({ module, action: 'approve' }),
    hasAnyPermission: hasAnyModulePermission(module),
  };
};